// ──────────────────────────────────────────────
// NetworkMonitor — online/offline detection with heartbeat verification
//
// navigator.onLine is unreliable (returns true with any network adapter active,
// even without internet). We layer a heartbeat ping as a secondary signal.
//
// Rules:
//   offline event → immediately report offline (fast signal)
//   online event  → start heartbeat, only report online when heartbeat succeeds
//   heartbeat failure while navigator.onLine → report offline (captive portal, firewall)
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import type { NetworkMonitorConfig, NetworkStatus } from './types';

export class NetworkMonitor {
  private status: NetworkStatus = 'unknown';
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<(isOnline: boolean) => void>();
  private config: NetworkMonitorConfig;
  private disposed = false;

  private onOnline = () => this.handleOnline();
  private onOffline = () => this.handleOffline();

  constructor(config: NetworkMonitorConfig) {
    this.config = config;
  }

  start(): void {
    if (this.disposed) return;

    // Set initial status from browser
    this.status = navigator.onLine ? 'online' : 'offline';

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);

    // If we appear online, verify with a heartbeat immediately
    if (this.status === 'online') {
      void this.performHeartbeat();
    }
  }

  stop(): void {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    this.stopHeartbeat();
  }

  /** Start periodic heartbeat checks. Call when uploads are active. */
  startHeartbeat(): void {
    if (this.disposed || this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      void this.performHeartbeat();
    }, this.config.healthCheckIntervalMs);
  }

  /** Stop periodic heartbeat. Call when upload queue is empty. */
  stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  isOnline(): boolean {
    return this.status === 'online';
  }

  getStatus(): NetworkStatus {
    return this.status;
  }

  onChange(handler: (isOnline: boolean) => void): () => void {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.listeners.clear();
  }

  // ── Private ──

  private handleOffline(): void {
    // Browser says offline → trust immediately (fast signal)
    this.setStatus('offline');
  }

  private handleOnline(): void {
    // Browser says online → verify with heartbeat before trusting
    void this.performHeartbeat();
  }

  private async performHeartbeat(): Promise<void> {
    if (this.disposed) return;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(this.config.healthCheckUrl, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        this.setStatus('online');
      } else {
        this.setStatus('offline');
      }
    } catch {
      // Network error, timeout, or abort → offline
      this.setStatus('offline');
    }
  }

  private setStatus(next: NetworkStatus): void {
    const wasOnline = this.status === 'online';
    const isNowOnline = next === 'online';
    this.status = next;

    if (wasOnline !== isNowOnline) {
      SentryService.addBreadcrumb({
        category: 'upload.network',
        message: `Network status changed: ${next}`,
        level: isNowOnline ? 'info' : 'warning',
      });

      for (const handler of this.listeners) {
        try {
          handler(isNowOnline);
        } catch (error) {
          SentryService.captureException(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      }
    }
  }
}
